import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface PartnerChurch {
  id: string;
  name: string;
  location: string;
  type: string;
  icon: string;
  image_url: string;
  description: string;
  display_order: number;
}

const ICON_MAP: { [key: string]: string } = {
  church: 'ri-community-line',
  global: 'ri-global-line',
  map: 'ri-map-pin-line',
  team: 'ri-team-line',
  hands: 'ri-hand-heart-line',
  building: 'ri-building-line',
  home: 'ri-home-heart-line',
  flag: 'ri-flag-line',
  compass: 'ri-compass-line',
  earth: 'ri-earth-line'
};

const TYPE_MAP: { [key: string]: string } = {
  church: '협력교회',
  mission: '협력선교지',
  organization: '협력기관',
  company: '협력업체'
};

export default function PartnerChurches() {
  const [partners, setPartners] = useState<PartnerChurch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const { data, error } = await supabase
        .from('partner_churches')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setPartners(data || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="partner-churches" className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-base sm:text-lg text-gray-600">로딩 중...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="partner-churches" className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            협력교회 및 협력선교지, 협력업체
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-teal-600 font-semibold">Partner Churches, Mission Fields &amp; Partners</p>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-3 sm:mt-4 max-w-3xl mx-auto leading-relaxed">
            킹덤브릿지미션은 국내외 교회, 선교단체 및 협력업체와 함께 
            하나님 나라 확장을 위한 영적 네트워킹을 이어가고 있습니다.
          </p>
        </div>

        {/* Partners Grid */}
        {partners.length === 0 ? (
          <div className="text-center py-12 sm:py-16 text-gray-500 text-sm sm:text-base">
            등록된 협력처가 없습니다.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {partners.map((partner) => (
              <div 
                key={partner.id}
                className="bg-gradient-to-br from-white to-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:shadow-xl hover:border-teal-200 transition-all group"
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Icon or Logo */}
                  {(partner.type === 'company' || partner.name === '다윗세대 청소년 선교단체') && partner.image_url ? (
                    <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden bg-white border border-gray-100">
                      <img 
                        src={partner.image_url} 
                        alt={partner.name} 
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                  ) : (
                    <div className="w-11 h-11 sm:w-14 sm:h-14 bg-teal-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-teal-600 transition-colors">
                      <i className={`${ICON_MAP[partner.icon] || 'ri-community-line'} text-lg sm:text-2xl text-teal-600 group-hover:text-white transition-colors`}></i>
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Type Badge */}
                    <span className={`inline-block px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold mb-1.5 sm:mb-2 ${
                      partner.type === 'company' 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-teal-600 text-white'
                    }`}>
                      {TYPE_MAP[partner.type] || partner.type}
                    </span>
                    
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                      {partner.name}
                    </h3>
                    
                    {/* Location */}
                    <div className="flex items-center gap-1 text-gray-500 text-xs sm:text-sm mb-2 sm:mb-3">
                      <i className="ri-map-pin-2-fill text-teal-600"></i>
                      <span>{partner.location}</span>
                    </div>
                    
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed line-clamp-2">
                      {partner.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-10 sm:mt-16 bg-gradient-to-br from-gray-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-center border-2 border-gray-100">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <i className="ri-links-line text-xl sm:text-2xl md:text-3xl text-teal-600"></i>
          </div>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
            함께 동역하고 싶으신가요?
          </h3>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 max-w-2xl mx-auto leading-relaxed mb-6 sm:mb-8">
            킹덤브릿지미션과 함께 나라와 나라, 교회와 교회를 연결하는 
            영적 네트워킹에 동참해 주세요.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a 
              href="tel:010-8524-9200"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-teal-600 text-white font-bold rounded-xl hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              <i className="ri-phone-line text-lg sm:text-xl"></i>
              <span>전화 문의</span>
            </a>
            <a 
              href="mailto:svkdavid@hanmail.net"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-teal-600 font-bold rounded-xl border-2 border-teal-600 hover:bg-teal-50 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              <i className="ri-mail-line text-lg sm:text-xl"></i>
              <span>이메일 문의</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
